import { memo } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { Tooltip, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { CHECK_BADGE_ICON_SIZE } from '@/constants';
import type { BasemapCardGridProps } from './BasemapCardGrid.types';
import classes from './BasemapCardGrid.module.css';

/**
 * Presentational grid of basemap thumbnail cards. Source-agnostic: it holds no
 * store logic and reports selection changes via {@link BasemapCardGridProps.onToggle}.
 * Backs both the layers-menu switcher (single) and the configurator allowlist (multi).
 */
export const BasemapCardGrid = memo(function BasemapCardGrid({
  items,
  selectedIds,
  onToggle,
  disabled = false,
  disabledTooltipKey,
  mode = 'single',
  className,
}: BasemapCardGridProps) {
  const { t } = useTranslation();
  const isMulti = mode === 'multi';

  return (
    <div className={clsx(classes.container, className)}>
      {items.map((item) => {
        const isSelected = selectedIds.includes(item.id);
        const label = t(item.labelKey);
        const tooltipLabel =
          disabled && disabledTooltipKey ? t(disabledTooltipKey) : label;
        return (
          <Tooltip key={item.id} label={tooltipLabel} withArrow>
            <UnstyledButton
              className={clsx(
                classes.card,
                isMulti && !isSelected && classes.dimmed
              )}
              onClick={() => onToggle(item.id)}
              disabled={disabled}
              aria-label={label}
              role={isMulti ? 'checkbox' : undefined}
              aria-checked={isMulti ? isSelected : undefined}
              aria-pressed={isMulti ? undefined : isSelected}
            >
              <span className={classes.thumbWrapper}>
                <img
                  src={item.thumbnail}
                  alt=""
                  className={classes.thumbnail}
                  loading="lazy"
                />
                {isSelected && (
                  <span className={classes.checkBadge}>
                    <Check size={CHECK_BADGE_ICON_SIZE} />
                  </span>
                )}
              </span>
              <span className={classes.label}>{label}</span>
            </UnstyledButton>
          </Tooltip>
        );
      })}
    </div>
  );
});
